import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { JwtModule } from '@auth0/angular-jwt';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { LanguageService } from './services/language.service';
import { getSubdomain } from './app-platform.util';
import { isMcpConsentPath } from './utils/msal-redirect.util';
import { FILE_UPLOAD_CONFIG, FileUploadConfig } from '@entitylink/dynamics';

const fileUploadConfig: FileUploadConfig = {
  fileUploadUrl: environment.uploadUrl,
  uploadImageUrl: environment.uploadImageUrl,
  uploadImageFetchUrl: environment.uploadImageFetchUrl,
};

export function MSALInstanceFactory(platformId: object): IPublicClientApplication {
  const redirectOrigin = isPlatformBrowser(platformId)
    ? window.location.origin
    : `https://${environment.webSiteDomain}`;

  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: `https://login.microsoftonline.com/common`,
      redirectUri: redirectOrigin + '/auth',
      postLogoutRedirectUri: redirectOrigin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              return;
            case LogLevel.Verbose:
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account',
    },
  };
}

export function preloadBrowserLanguages(languageService: LanguageService, platformId: object) {
  return async () => {
    await languageService.loadConfiguration();
    if (!isPlatformBrowser(platformId)) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const showLocaleTags = urlParams.get('showlocaletags') === 'true';
    if (showLocaleTags) {
      languageService.setShowLocaleTags(true);
      return;
    }
    languageService.setBrowserLocales(navigator.languages);
    const subdomain = getSubdomain(platformId);
    languageService.setDefaultLocale(languageService.getLocaleDefaultForSubdomain(subdomain));
  };
}

export function msalInitializerFactory(msalService: MsalService, platformId: object) {
  return async () => {
    if (!isPlatformBrowser(platformId)) {
      return;
    }
    await msalService.instance.initialize();
    // MSAL requires handleRedirectPromise after initialize on every page.
    // Skip only Claude consent — its `state` query is not an MSAL callback.
    if (isMcpConsentPath(window.location.pathname)) {
      return;
    }
    await msalService
      .handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .toPromise();
  };
}

export function registerAppControlsInitializerFactory(platformId: object) {
  return async () => {
    if (!isPlatformBrowser(platformId)) {
      return;
    }
    const { registerAppControls } = await import('./app-control-registry');
    registerAppControls();
  };
}

/** Browser-only auth providers (MSAL, JWT). Not loaded during SSR route extraction. */
export const browserAppConfig: ApplicationConfig = {
  providers: [
    { provide: FILE_UPLOAD_CONFIG, useValue: fileUploadConfig },
    {
      provide: APP_INITIALIZER,
      useFactory: registerAppControlsInitializerFactory,
      deps: [PLATFORM_ID],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: preloadBrowserLanguages,
      deps: [LanguageService, PLATFORM_ID],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializerFactory,
      deps: [MsalService, PLATFORM_ID],
      multi: true,
    },
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: () => localStorage.getItem('token'),
          allowedDomains: ['localhost:4200', 'localhost:4201', 'localhost:4210'],
          disallowedRoutes: [],
        },
      }),
      MsalModule,
    ),
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory, deps: [PLATFORM_ID] },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
